import React from 'react';
import { ZoomIn, ZoomOut, RotateCw, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';

interface ZoomControlsProps {
  zoomLevel: number;
  onZoomChange: (level: number) => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
}

const zoomLevels = [0.5, 0.6, 0.7, 0.8, 0.9, 1.0, 1.1, 1.25, 1.5];

export const ZoomControls: React.FC<ZoomControlsProps> = ({
  zoomLevel,
  onZoomChange,
  isFullscreen,
  onToggleFullscreen,
}) => {
  const zoomIn = () => {
    const currentIndex = zoomLevels.findIndex(level => level >= zoomLevel);
    const nextIndex = Math.min(currentIndex + 1, zoomLevels.length - 1);
    onZoomChange(zoomLevels[nextIndex]);
  };

  const zoomOut = () => {
    const currentIndex = zoomLevels.findIndex(level => level >= zoomLevel);
    const prevIndex = Math.max(currentIndex - 1, 0);
    onZoomChange(zoomLevels[prevIndex]);
  };

  const resetZoom = () => {
    onZoomChange(1.0);
  };

  const handleSliderChange = (values: number[]) => {
    onZoomChange(values[0]);
  };

  return (
    <Card className="fixed bottom-6 right-6 p-3 bg-card/95 backdrop-blur-sm border shadow-lg z-50">
      <div className="flex items-center gap-3">
        {/* Zoom Out */}
        <Button
          variant="ghost"
          size="sm"
          onClick={zoomOut}
          disabled={zoomLevel <= zoomLevels[0]}
          className="h-8 w-8 p-0"
        >
          <ZoomOut className="h-4 w-4" />
        </Button>

        {/* Zoom Slider */}
        <div className="flex items-center gap-2 min-w-24">
          <Slider
            value={[zoomLevel]}
            onValueChange={handleSliderChange}
            min={0.5}
            max={1.5}
            step={0.1}
            className="w-16"
          />
        </div>

        {/* Zoom Level Display */}
        <Badge variant="secondary" className="text-xs font-mono min-w-12 justify-center">
          {Math.round(zoomLevel * 100)}%
        </Badge>

        {/* Zoom In */}
        <Button
          variant="ghost"
          size="sm"
          onClick={zoomIn}
          disabled={zoomLevel >= zoomLevels[zoomLevels.length - 1]}
          className="h-8 w-8 p-0"
        >
          <ZoomIn className="h-4 w-4" />
        </Button>

        {/* Reset Zoom */}
        <Button
          variant="ghost"
          size="sm"
          onClick={resetZoom}
          className="h-8 w-8 p-0"
          title="إعادة تعيين التكبير"
        >
          <RotateCw className="h-4 w-4" />
        </Button>

        {/* Fullscreen Toggle */}
        <div className="w-px h-6 bg-border mx-1" />
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleFullscreen}
          className="h-8 w-8 p-0"
          title={isFullscreen ? "إنهاء ملء الشاشة" : "ملء الشاشة"}
        >
          {isFullscreen ? (
            <Minimize2 className="h-4 w-4" />
          ) : (
            <Maximize2 className="h-4 w-4" />
          )}
        </Button>
      </div>
    </Card>
  );
};